import React, { useState, useEffect } from "react";
import GovernmentOfIndia from "../../../assets/govt-of-india.svg";
import Constrast from "../../../assets/contrast.png";
import Accessiblity from "../../../assets/accessibility_new.png";
import { openAccessibilityWidget } from "../../../utils/accessibilityWidget";

// Government-of-India accessibility bar for the auth pages (login /
// forget-password / reset-password): Skip to Main Content, font size, contrast,
// accessibility and the Bhashini language widget.
//
// This bar renders on mobile AND desktop on purpose. It must be the only
// .bhashini-plugin-container on the page: the Bhashini script injects into the
// FIRST match it finds, so a second (even display:none) container would swallow
// the widget and leave the visible one empty.
const AuthGovBar: React.FC = () => {
  const [fontLevel, setFontLevel] = useState<"normal" | "large" | "small">(
    "normal",
  );
  const [darkMode, setDarkMode] = useState(false);

  const applyFontSize = (level: "normal" | "large" | "small") => {
    document.documentElement.classList.remove(
      "a11y-text-large",
      "a11y-text-small",
    );
    if (level === "large")
      document.documentElement.classList.add("a11y-text-large");
    else if (level === "small")
      document.documentElement.classList.add("a11y-text-small");
    setFontLevel(level);
  };

  const toggleDarkMode = () => {
    const next = !darkMode;
    if (next) document.body.classList.add("a11y-dark");
    else document.body.classList.remove("a11y-dark");
    setDarkMode(next);
  };

  const skipToMain = () => {
    // Moving focus (not just scrolling) is the actual point of a skip link -
    // it is what lets keyboard/screen-reader users bypass the header/nav, and
    // it works regardless of current scroll position. main-content has
    // tabIndex={-1} in App.tsx so it can receive focus programmatically;
    // focusing it also scrolls it into view, so no separate scrollTo is needed.
    document.getElementById("main-content")?.focus();
  };

  // Load the Bhashini translation widget into the container below.
  useEffect(() => {
    const scriptId = "bhashini-translation-script";
    const container = document.querySelector(".bhashini-plugin-container");
    if (container) container.innerHTML = "";
    document.getElementById(scriptId)?.remove();
    const script = document.createElement("script");
    script.id = scriptId;
    script.src =
      "https://translation-plugin.bhashini.co.in/v3/website_translation_utility.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <div className="flex shrink-0 w-full bg-[#003366] text-white px-3 md:px-6 py-2 items-center justify-between text-xs md:text-sm overflow-visible">
      <button
        type="button"
        className="hover:opacity-80 transition cursor-pointer shrink-0"
        onClick={() => { window.location.href = "https://www.india.gov.in/"; }}
      >
        <img
          src={GovernmentOfIndia}
          width={205}
          height={22}
          alt="Government of India"
          className="h-4 md:h-auto"
        />
      </button>

      {/* Fixed height + items-stretch so every divider is the same height
          regardless of item content. */}
      <div className="flex items-stretch h-5 divide-x divide-[#F9FAFB] overflow-visible">
        {/* Skip link is desktop-only — there is no room for it on mobile. */}
        <button
          onClick={skipToMain}
          className="hidden md:flex items-center px-4 hover:opacity-80 transition whitespace-nowrap cursor-pointer"
        >
          Skip to Main Content
        </button>

        <div className="flex items-stretch divide-x divide-[#F9FAFB] overflow-visible">
          <button
            onClick={() => applyFontSize("small")}
            className={`flex items-center justify-center px-2 md:px-3 hover:opacity-80 transition text-[#F9FAFB] cursor-pointer${
              fontLevel === "small" ? " a11y-btn-active" : ""
            }`}
            title="Decrease text size"
            aria-pressed={fontLevel === "small"}
          >
            A<sup>-</sup>
          </button>
          <button
            onClick={() => applyFontSize("normal")}
            className={`flex items-center justify-center px-2 md:px-3 hover:opacity-80 transition text-[#F9FAFB] cursor-pointer${
              fontLevel === "normal" ? " a11y-btn-active" : ""
            }`}
            title="Reset text size"
            aria-pressed={fontLevel === "normal"}
          >
            A
          </button>
          <button
            onClick={() => applyFontSize("large")}
            className={`flex items-center justify-center px-2 md:px-3 hover:opacity-80 transition text-[#F9FAFB] cursor-pointer${
              fontLevel === "large" ? " a11y-btn-active" : ""
            }`}
            title="Increase text size"
            aria-pressed={fontLevel === "large"}
          >
            A<sup>+</sup>
          </button>

          <button
            onClick={toggleDarkMode}
            className={`flex items-center justify-center px-2 md:px-3 hover:opacity-80 transition cursor-pointer${
              darkMode ? " a11y-btn-active" : ""
            }`}
            title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            aria-pressed={darkMode}
          >
            <img
              src={Constrast}
              width={20}
              height={20}
              alt="Contrast"
              className="h-3 md:h-4"
            />
          </button>

          <button
            type="button"
            onClick={openAccessibilityWidget}
            className="flex items-center justify-center px-2 md:px-3 hover:opacity-80 transition cursor-pointer"
            title="Accessibility"
          >
            <img
              src={Accessiblity}
              width={20}
              height={20}
              alt="Accessibility"
              className="h-3 md:h-4"
            />
          </button>

          <div className="bhashini-plugin-container flex items-center justify-center px-2 md:px-3 relative z-[9999] overflow-visible"></div>
        </div>
      </div>
    </div>
  );
};

export default AuthGovBar;
